// App.js - The Authorization Hook v2.0
// Multi-Tenant Architecture with Square Subscriptions & Kill Switch

import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut, connectAuthEmulator } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';

// Components
import IntakeForm from './IntakeForm';
import AdvisorDash from './AdvisorDash';
import AdminPanel from './AdminPanel';
import ShopOnboarding from './ShopOnboarding';
import ServiceSuspended from './ServiceSuspended';
import SubscriptionGuard from './SubscriptionGuard';
import Login from './Login';
import LandingPage from './LandingPage';
import Signup from './Signup';
import PaymentComplete from './PaymentComplete';
import Settings from './Settings';

// Firebase Configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "YOUR_PROJECT.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "YOUR_PROJECT_ID",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "YOUR_PROJECT.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "YOUR_SENDER_ID",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "YOUR_APP_ID"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const auth = getAuth(app);
export const functions = getFunctions(app);

// Connect to local emulators in development
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectStorageEmulator(storage, 'localhost', 9199);
  console.log('%c[Emulators] Connected to local Firebase emulators', 'color: #f59e0b; font-weight: bold');
}

// ============================================================================
// AUTHENTICATION CONTEXT
// ============================================================================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [shopId, setShopId] = useState(null);
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let unsubscribeUserDoc;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        // Real-time listener for the user document to handle race conditions
        unsubscribeUserDoc = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUser({ uid: user.uid, email: user.email, ...data }); // Merge auth and doc data
            setUserRole(data.role);
            setShopId(data.shopId);
            setLoading(false);
          } else {
            // Doc doesn't exist yet - could be creation latency. 
            // We stay in loading state rather than erroring out immediately.
            console.log("Waiting for user profile...");
          }
        }, (error) => {
          console.error("Error fetching user profile:", error);
          setAuthError('Failed to load user profile: ' + error.message);
          setLoading(false);
        });
      } else {
        setUser(null);
        setUserRole(null);
        setShopId(null);
        setShopData(null);
        setLoading(false);
        if (unsubscribeUserDoc) unsubscribeUserDoc();
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  // Fetch shop data when shopId is available
  useEffect(() => {
    if (!shopId) {
      setShopData(null);
      return;
    }

    const unsubscribeShop = onSnapshot(doc(db, 'shops', shopId), (docSnap) => {
      if (docSnap.exists()) {
        setShopData({ id: docSnap.id, ...docSnap.data() });
      } else {
        setShopData(null);
      }
    }, (error) => {
      console.error("Error fetching shop data:", error);
    });

    return () => unsubscribeShop();
  }, [shopId]);

  const login = async (email, password) => {
    setLoading(true);
    setAuthError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserRole(null);
    setShopId(null);
    setShopData(null);
    setAuthError(null);
    // Clear any cached data
    localStorage.clear();
    sessionStorage.clear();
  };

  const value = {
    user,
    userRole,
    shopId,
    shopData,
    loading,
    authError,
    login,
    logout,
    isAdmin: userRole === 'admin',
    isOwner: userRole === 'owner',
    isAdvisor: userRole === 'advisor'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// SHOP CONTEXT (For tenant isolation)
// ============================================================================
const ShopContext = createContext(null);

export const useShop = () => {
  const context = useContext(ShopContext);
  return context;
};

// Wrapper that extracts shopId from URL for public pages
const ShopProvider = ({ children }) => {
  const { shopId } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    const shopRef = doc(db, 'shops', shopId);
    const unsubscribe = onSnapshot(shopRef, (shopDoc) => {
      if (shopDoc.exists()) {
        setShop({ id: shopDoc.id, ...shopDoc.data() });
        setError(null);
      } else {
        setShop(null);
        setError('Shop not found');
      }
      setLoading(false);
    }, (err) => {
      console.error('Shop fetch error:', err);
      setError('Failed to load shop');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [shopId]);

  return (
    <ShopContext.Provider value={{ shop, loading, error, shopId }}>
      {children}
    </ShopContext.Provider>
  );
};

// ============================================================================
// PROTECTED ROUTE COMPONENTS
// ============================================================================

// Requires authentication
const PrivateRoute = ({ children, allowedRoles = [] }) => {
  const { user, userRole, loading, authError } = useAuth();

  // Still loading - show spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-amber-400 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Auth error (user doc not found, etc)
  if (authError) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Account Error</h2>
          <p className="text-slate-600 mb-4">{authError}</p>
          <button 
            onClick={() => window.location.href = '/login'} 
            className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Check role if specified
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Admin-only route
const AdminRoute = ({ children }) => {
  return (
    <PrivateRoute allowedRoles={['admin']}>
      {children}
    </PrivateRoute>
  );
};

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public shop-specific routes (with shopId in URL) */}
          <Route path="/s/:shopId/*" element={
            <ShopProvider>
              <Routes>
                {/* Customer intake - no subscription guard, handled in IntakeForm */}
                <Route path="intake" element={<IntakeForm />} />
                
                {/* Suspended page */}
                <Route path="suspended" element={<ServiceSuspended />} />
              </Routes>
            </ShopProvider>
          } />

          {/* Protected advisor dashboard */}
          <Route path="/dashboard" element={
            <PrivateRoute allowedRoles={['owner', 'advisor', 'admin']}>
              <SubscriptionGuard useAuthShop={true}>
                <AdvisorDash />
              </SubscriptionGuard>
            </PrivateRoute>
          } />

          {/* Shop onboarding (payment setup) */}
          <Route path="/onboarding" element={
            <PrivateRoute allowedRoles={['owner']}>
              <ShopOnboarding />
            </PrivateRoute>
          } />

          {/* Admin panel - Larry's God Mode */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />

          {/* Authentication */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/payment-complete" element={<PaymentComplete />} />
          
          {/* Settings */}
          <Route path="/settings" element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          } />
          
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Unauthorized */}
          <Route path="/unauthorized" element={
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
                <p className="text-slate-400 mb-6">You don't have permission to view this page.</p>
                <button 
                  onClick={() => window.location.href = '/login'} 
                  className="px-6 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400"
                >
                  Back to Login
                </button>
              </div>
            </div>
          } />

          {/* Legacy/redirect routes */}
          <Route path="/intake" element={<Navigate to="/login" replace />} />
          
          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-amber-400 mb-4">404</h1>
                <p className="text-slate-400 mb-6">Page not found</p>
                <button 
                  onClick={() => window.location.href = '/login'} 
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Go Home
                </button>
              </div>
            </div>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
